import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

import { useAuth } from '@/hooks/useAuth';
import { useMyProfile } from '@/hooks/profile';
import { useWeeklyHours, useScheduledDays } from '@/hooks/dashboard';
import { generateInitials, generateDisplayName } from '@/utils/avatarUtils';
import TimeAttendance from '@/components/TimeAttendance';
import { ProfileCard } from '@/components/dashboard';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Calendar, BarChart3 } from 'lucide-react';

const Index = () => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth(); // Keep for company data
  const { data: profile, isLoading: profileLoading, error: profileError } = useMyProfile();
  const { data: weeklyHoursData, isLoading: weeklyHoursLoading } = useWeeklyHours(profile?.id);
  const { data: scheduledDaysData, isLoading: scheduledDaysLoading } = useScheduledDays(profile?.id);

  // ProfileAvatar component with loading and error states
  const ProfileAvatar = () => {
    if (profileLoading) {
      return <Skeleton className="h-12 w-12 rounded-full" />;
    }

    if (profileError || !profile) {
      return (
        <Avatar className="h-12 w-12">
          <AvatarFallback>
            {generateInitials('User', '')}
          </AvatarFallback>
        </Avatar>
      );
    }

    return (
      <Avatar className="h-12 w-12">
        <AvatarImage 
          src={profile.avatar_url || undefined} 
          alt={generateDisplayName(profile.first_name, profile.last_name)}
        />
        <AvatarFallback>
          {generateInitials(profile.first_name, profile.last_name)}
        </AvatarFallback>
      </Avatar>
    );
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section with Profile Avatar */}
      <div className="flex items-center gap-4">
        <ProfileAvatar />
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">
            {currentCompany?.name
              ? t('dashboard.welcomeTo', { name: currentCompany.name, defaultValue: `Welcome to ${currentCompany.name}` })
              : t('dashboard.welcome', 'Welcome')}
          </h1>
          <div className="text-muted-foreground">
            {profileLoading ? (
              <Skeleton className="h-4 w-48" />
            ) : profile ? (
              `Welcome back, ${profile.first_name || 'User'}!`
            ) : (
              'Welcome back!'
            )}
          </div>
        </div>
      </div>

      {/* Time & Attendance Section */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ErrorBoundary>
            <TimeAttendance />
          </ErrorBoundary>
        </div>
        
        {/* Quick Stats */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Schedule</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {scheduledDaysLoading ? (
                <Skeleton className="h-8 w-12" />
              ) : (
                <div className="text-2xl font-bold">{scheduledDaysData?.daysCount ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                Days this week
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Week</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {weeklyHoursLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{weeklyHoursData?.formattedHours ?? '0h 0m'}</div>
              )}
              <p className="text-xs text-muted-foreground">
                Total hours worked
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Profile Card Section */}
      <ProfileCard />

    </div>
  );
};

export default Index;
